/**
 * Sidebar + topbar walkthrough — surfaced for first-time customers
 * the moment /home renders. CPF-FR-151.
 *
 * Selectors target stable `data-tour="..."` attributes on nav items
 * (added in `features/home/Sidebar.tsx` + `features/home/Topbar.tsx`)
 * rather than text or class names which are likely to drift as the
 * UI evolves.
 *
 * Bump the suffix on `SIDEBAR_TOUR_ID` (e.g. `-v1` → `-v2`) when
 * the steps change materially so existing customers see the
 * updated walkthrough on their next visit. The previous-version
 * key in localStorage stays orphaned but harmless.
 *
 * Step order mirrors the v3 navigation reshape (Dashboard → Users
 * → Monitoring → Auth → Extraction → Delivery → Score → Develop)
 * so the order the customer SEES in the sidebar matches the order
 * they ENCOUNTER in the tour.
 */

export type TourStepSide = "top" | "bottom" | "left" | "right";

export type TourStep = {
  selector: string;
  title: string;
  body: string;
  preferredSide?: TourStepSide;
};

export const SIDEBAR_TOUR_ID = "sidebar-walkthrough-v1";

export const SIDEBAR_TOUR_STEPS: TourStep[] = [
  {
    selector: '[data-tour="nav-dashboard"]',
    title: "Workspace overview",
    body:
      "Your daily-driver page — API inquiries, balance, connected users, and recent activity. Start here every login to answer \"is my integration healthy right now?\".",
    preferredSide: "right",
  },
  {
    selector: '[data-tour="nav-users"]',
    title: "Users",
    body:
      "Every user who connected a data source through your integration. Click a row to inspect their authorisations, last extraction, and per-source status.",
    preferredSide: "right",
  },
  {
    selector: '[data-tour="nav-monitoring"]',
    title: "Monitoring",
    body:
      "Logs (the per-document delivery trail to your webhook) and Status page (ROOK platform health). The first place to look when a delivery seems off.",
    preferredSide: "right",
  },
  {
    selector: '[data-tour="nav-auth"]',
    title: "Auth credentials",
    body:
      "API and SDK credentials your backend uses to call ROOK. Each environment (Sandbox / Production) has its own pair — credentials issued in one environment will not authenticate against the other.",
    preferredSide: "right",
  },
  {
    selector: '[data-tour="nav-extraction"]',
    title: "Extraction",
    body:
      "How and when ROOK pulls from a user's history once they connect. Set the timezone for sleep summaries and turn on pre-existing data backfill before your first user authorises.",
    preferredSide: "right",
  },
  {
    selector: '[data-tour="nav-delivery"]',
    title: "Delivery",
    body:
      "Where and how ROOK posts processed health documents to your infrastructure. Webhook endpoints, document structure, and granular event opt-ins all live here.",
    preferredSide: "right",
  },
  {
    selector: '[data-tour="nav-score"]',
    title: "ROOK Score",
    body:
      "Aggregated health score we compute from a user's data. Configure pillar weights so the score reflects what matters for your product.",
    preferredSide: "right",
  },
  {
    selector: '[data-tour="nav-develop"]',
    title: "Develop",
    body:
      "Tools to test your integration before your first user signs up: a JSON simulator that posts synthetic documents to your webhook, and a Connection Page demo so you can preview the OAuth authorisation flow.",
    preferredSide: "right",
  },
  {
    selector: '[data-tour="topbar-env"]',
    title: "Environment switch",
    body:
      "Toggle between the Sandbox and Production environments. Every page in this portal reads the active environment — credentials, webhook URLs, user data, and Logs all swap when you flip this.",
    preferredSide: "bottom",
  },
  {
    selector: '[data-tour="topbar-account"]',
    title: "Your account",
    body:
      "Profile, company information, plan, and billing. Click here for password change and your subscription tier. You can re-launch this tour from Account at any time.",
    preferredSide: "bottom",
  },
];
